import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DayPicker, useDayRender } from "react-day-picker";
import moment from "moment";
import { DATE_FORMATS } from "../../../../../constants";
import SvgIcon from "../../../../../parts/SvgIcon";
import MonthSelectionBar from "./MonthSelectionBar";
import {
  canNavigateBackward,
  canNavigateForward,
} from "../menstrual-cycle.helper";

const { DATE_YEAR_FORMAT, DATE_DAY_FORMAT } = DATE_FORMATS;

// Symptoms mapping with icons
const symptoms = [
  { value: "Cramps", icon: "🩸" },
  { value: "Bloating", icon: "🎈" },
  { value: "Headache", icon: "🤯" },
  { value: "Fatigue", icon: "😴" },
  { value: "Mood", icon: "🎭" },
  { value: "Skin", icon: "✨" },
  { value: "Breast Pain", icon: "😣" },
  { value: "Back Pain", icon: "🦴" },
  { value: "Cravings", icon: "🍫" },
  { value: "Sleep", icon: "🛌" },
];

// Create a map for quick lookup
const symptomIconMap = new Map(symptoms.map((s) => [s.value, s.icon]));

/**
 * Custom Day Component for CycleCalendar
 * Wraps the default day button and adds a blood icon at the bottom for logged period days
 */
const CustomDay = (props) => {
  const { date, displayMonth } = props;
  const buttonRef = React.useRef(null);
  const dayRender = useDayRender(date, displayMonth, buttonRef);

  if (dayRender.isHidden) {
    return <React.Fragment />;
  }

  // Get the day number
  const dayNumber = date.getDate();

  if (!dayRender.isButton) {
    return <div {...dayRender.divProps}>{dayNumber}</div>;
  }

  // Check if this day has the logged-period-day or predicted-period-day modifier
  const className = dayRender.buttonProps.className || "";
  const isPeriodDay =
    className.includes("logged-period-day") ||
    className.includes("predicted-period-day");

  return (
    <div className="cycle-calendar-day-wrapper">
      <button
        {...dayRender.buttonProps}
        ref={buttonRef}
        className={`${dayRender.buttonProps.className || ""} cycle-calendar-day-button`}
      >
        {dayNumber}
      </button>
      {isPeriodDay && (
        <div className="cycle-calendar-blood-icon">
          <SvgIcon
            classNames="blood-icon"
            src="/img/new-icons/blood-drop-icon.svg"
            noFilterOnLight={true}
          />
        </div>
      )}
    </div>
  );
};

/**
 * CycleCalendar Component
 * Displays 6 months of menstrual cycle data (current + 5 future)
 * Shows logged periods and predicted periods with different styling
 */
const CycleCalendar = ({
  selectedMonth,
  calendarData,
  availableMonths,
  startingCalendarDay,
  theme,
}) => {
  const { t } = useTranslation("athlete-portal");
  // State to track which months have details expanded
  const [expandedMonths, setExpandedMonths] = useState(new Set());

  // Generate array of 6 months to display
  const monthsToDisplay = [selectedMonth];
  for (let i = 1; i <= 5; i++) {
    monthsToDisplay.push(
      moment(selectedMonth, DATE_YEAR_FORMAT)
        .add(i, "months")
        .format(DATE_YEAR_FORMAT)
    );
  }

  // Build a local-date Date so the calendar highlights the correct day in any timezone.
  // new Date("YYYY-MM-DD") parses as UTC midnight and shifts the day in western timezones.
  const toLocalDate = (dateStr) => {
    const m = moment(dateStr, "YYYY-MM-DD");
    return new Date(m.year(), m.month(), m.date());
  };

  // Prepare modifiers for DayPicker
  const prepareModifiers = (monthKey) => {
    const data = calendarData[monthKey] || {
      periodDays: [],
      predictedPeriodDays: [],
    };

    return {
      periodDays: data.periodDays.map(toLocalDate),
      predictedPeriodDays: data.predictedPeriodDays.map(toLocalDate),
    };
  };

  const canGoBack = canNavigateBackward(selectedMonth, availableMonths);
  const canGoForward = canNavigateForward(selectedMonth, availableMonths);

  // Format weekday names to 3 letters (MON, TUE, etc.)
  const formatWeekdayName = (date) => {
    const weekdayNames = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    return weekdayNames[date.getDay()];
  };

  // Toggle details visibility for a specific month
  const handleShowDetails = (monthKey, isPredicted = false) => {
    if (isPredicted) {
      return;
    }

    setExpandedMonths((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(monthKey)) {
        newSet.delete(monthKey);
      } else {
        newSet.add(monthKey);
      }
      return newSet;
    });
  };

  // Get flow intensity display (with emoji indicators)
  const getFlowIntensityDisplay = (intensity) => {
    if (!intensity) return null;

    // Map intensity to emoji indicators
    const intensityMap = {
      low: (
        <SvgIcon
          classNames="blood-icon"
          src="/img/new-icons/blood-drop-icon.svg"
          noFilterOnLight={true}
        />
      ),
      moderate: (
        <SvgIcon
          classNames="blood-icon"
          src={
            theme === "dark"
              ? "/img/new-icons/blood-drop-icon-double-dark.svg"
              : "/img/new-icons/blood-drop-icon-double-light.svg"
          }
          noFilterOnLight={true}
        />
      ),
      high: (
        <SvgIcon
          classNames="blood-icon"
          src={
            theme === "dark"
              ? "/img/new-icons/blood-drop-icon-triple-dark.svg"
              : "/img/new-icons/blood-drop-icon-triple-light.svg"
          }
          noFilterOnLight={true}
        />
      ),
    };

    const emojis = intensityMap[intensity] || (
      <SvgIcon
        classNames="blood-icon"
        src="/img/new-icons/blood-drop-icon.svg"
        noFilterOnLight={true}
      />
    );
    return { emojis, text: intensity };
  };

  return (
    <div className="cycle-calendar-container">
      <div className="cycle-calendar-legend">
        <div className="w-1/3 legend-items">
          <div className="legend-item">
            <div className="legend-color logged-period"></div>
            <p>{t("cycleCalendar.loggedPeriod")}</p>
          </div>
          <div className="legend-item">
            <div className="legend-color predicted-period"></div>
            <p>{t("cycleCalendar.predictedPeriod")}</p>
          </div>
        </div>

        <MonthSelectionBar
          selectedMonth={selectedMonth}
          canGoBack={canGoBack}
          canGoForward={canGoForward}
        />

        <div className="w-1/3"></div>
      </div>

      <div className="cycle-calendar-grid">
        {monthsToDisplay.map((monthKey) => {
          const monthDate = moment(monthKey, DATE_YEAR_FORMAT).toDate();
          const modifiers = prepareModifiers(monthKey);

          const hasLogs = calendarData[monthKey]?.logs?.length > 0;
          const isPredicted = !hasLogs && monthKey !== selectedMonth;
          const hasSymptoms = calendarData[monthKey]?.symptoms?.length > 0;
          const hasFlowIntensity = calendarData[monthKey]?.intensityFlow;

          return (
            <div
              key={monthKey}
              className={`cycle-calendar-month${isPredicted ? " predicted-month" : ""}`}
            >
              <div className="cycle-calendar-month-header">
                <h4 className="text-center">
                  {moment(monthKey, DATE_YEAR_FORMAT).format("MMMM YYYY")}
                </h4>
                {!hasLogs && monthKey !== selectedMonth && (
                  <span className="prediction-badge">{t("cycleCalendar.predicted")}</span>
                )}
              </div>
              <DayPicker
                month={monthDate}
                numberOfMonths={1}
                showOutsideDays={true}
                modifiers={modifiers}
                modifiersClassNames={{
                  periodDays: "logged-period-day",
                  predictedPeriodDays: "predicted-period-day",
                }}
                onDayClick={() => handleShowDetails(monthKey, isPredicted)}
                disableNavigation={true}
                components={{ Day: CustomDay }}
                weekStartsOn={startingCalendarDay}
                formatters={{ formatWeekdayName }}
              />

              {(hasSymptoms || hasFlowIntensity) && (
                <>
                  <button
                    type="button"
                    className="cycle-calendar-month-footer"
                    onClick={() => handleShowDetails(monthKey)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleShowDetails(monthKey);
                      }
                    }}
                  >
                    <p>
                      {expandedMonths.has(monthKey)
                        ? t("cycleCalendar.hideDetails")
                        : t("cycleCalendar.seeDetails")}
                    </p>
                  </button>
                  {expandedMonths.has(monthKey) && (
                    <div className="cycle-calendar-details">
                      {hasFlowIntensity && (
                        <div className="cycle-calendar-detail-item items-center">
                          <p className="cycle-calendar-detail-label p2">
                            {t("cycleCalendar.flowIntensity")}
                          </p>
                          <div className="cycle-calendar-detail-value">
                            {(() => {
                              const flowDisplay = getFlowIntensityDisplay(
                                calendarData[monthKey]?.intensityFlow
                              );
                              return flowDisplay ? (
                                <p className="cycle-calendar-symptom">
                                  {flowDisplay.emojis}{" "}
                                  {flowDisplay.text.charAt(0).toUpperCase() +
                                    flowDisplay.text.slice(1)}
                                </p>
                              ) : null;
                            })()}
                          </div>
                        </div>
                      )}
                      {hasSymptoms && (
                        <div className="cycle-calendar-detail-item">
                          <p className="cycle-calendar-detail-label p2">
                            {t("cycleCalendar.symptoms")}
                          </p>
                          <div className="cycle-calendar-detail-value">
                            {calendarData[monthKey]?.symptoms
                              ?.map((symptom) => {
                                const icon = symptomIconMap.get(symptom) || "";
                                return (
                                  <p
                                    key={symptom}
                                    className="cycle-calendar-symptom"
                                  >
                                    {icon} {symptom}
                                  </p>
                                );
                              })
                              .reduce((acc, elem, index) => {
                                if (index === 0) {
                                  return [elem];
                                }
                                acc.push(", ", elem);
                                return acc;
                              }, [])}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CycleCalendar;
